import React, { ReactNode } from "react";
import { Close, IconSize, Warning } from "../../icons";
import classNames from "classnames";
import { observer } from "mobx-react";
import { IconButton } from "../../..";
import { ButtonSize, ButtonTheme, ButtonType } from "../Button/Button";

/**
 * Represents the alignment of the text and components inside an alert.
 */
export enum AlertAlignment {
  Left = "left",
  Center = "center",
}

/**
 * Represents the type of alert that should be displayed in an `Alert` component.
 */
export enum AlertType {
  /**
   * Useful for hinting to an action that a user might take.
   */
  Info = "info",
  /**
   * Useful for warning the user in response to an action they've taken.
   * This will trigger the ARIA "alert" role for accessibility services.
   */
  Error = "error",
}

export enum AlertTheme {
  Light = "light",
  Dark = "dark",
}

interface IAlertProps {
  /**
   * A heading for the alert.
   */
  heading: string;
  /**
   * One or more React nodes that represent the body of the alert.
   */
  children: ReactNode;
  /**
   * The alignment of the body and heading with respect to the alert.
   */
  alignment?: AlertAlignment;
  /**
   * The type of alert to be displayed. See `AlertType` for the potential
   * range of types.
   */
  alertType?: AlertType;
  /**
   * Optional prop to extend the styling of the alert container.
   */
  className?: string;
  /**
   * Whether light or dark theme. Note that currently the dark
   * theme does not support `AlertType.Error`.
   * Defaults to `AlertTheme.Light`.
   */
  theme?: AlertTheme;
  onDismiss?: (event: React.MouseEvent<HTMLElement>) => void;
}

/**
 * A component useful for alerting a user, either to help guide them to an action,
 * or in response to a given action.
 */
const Alert = observer(
  ({
    heading,
    children,
    alignment = AlertAlignment.Left,
    alertType = AlertType.Info,
    className,
    theme = AlertTheme.Light,
    onDismiss,
  }: IAlertProps): JSX.Element => (
    <div
      className={classNames("alert", className, {
        "alert--centre-aligned": alignment === AlertAlignment.Center,
        "alert--error-type": alertType === AlertType.Error,
        "alert--theme-dark": theme === AlertTheme.Dark,
      })}
      role={alertType === AlertType.Error ? "alert" : undefined}
    >
      <div
        className={classNames("alert__heading", {
          "alert__heading--theme-dark": theme === AlertTheme.Dark,
          "alert__heading--dismissable": !!onDismiss,
        })}
      >
        {alertType === AlertType.Error && (
          <div className="alert__warning">
            <Warning />
          </div>
        )}
        {heading}
        {onDismiss && (
          <div className="alert__dismiss">
            <IconButton
              className="alert__dismiss-button"
              onClick={onDismiss}
              icon={<Close size={IconSize.Small} />}
              size={ButtonSize.Small}
              type={ButtonType.Tertiary}
              aria-label="Dismiss alert"
              theme={theme === AlertTheme.Dark ? ButtonTheme.Dark : undefined}
            />
          </div>
        )}
      </div>
      <div
        className={classNames("alert__body", {
          "alert__body--error-type": alertType === AlertType.Error,
          "alert__body--centre-aligned": alignment === AlertAlignment.Center,
          "alert__body--theme-dark": theme === AlertTheme.Dark,
        })}
      >
        {children}
      </div>
    </div>
  ),
);

export { Alert };
